import { CrewMemberWithShifts } from '@/common/types/crew-shifts';
import { ShiftsCrewWarningsType } from '@/crew/store/shifts/types';
import { CrewRotation, CrewStatusAction, CrewContract } from '@/common/types/crew';
import { isAfter, isBefore, parseISO, startOfDay, endOfDay, isSameDay, isValid } from 'date-fns';
import _get from 'lodash/get';
import { Port } from '@/common/types/ports.ts';

type DocumentWarningCategoriesType = 'expired' | 'missing' | 'reminder' | 'valid';

type DocumentWarningsType = {
  counts: {
    [key in DocumentWarningCategoriesType]:
      | {
          [key in 's' | 'm' | 'r']: number;
        }
      | null;
  };
};

export const getTotalDocumentWarningsPerType = (
  documentWarnings: DocumentWarningsType | undefined,
  warningType: DocumentWarningCategoriesType
) => {
  const warningCounts = documentWarnings?.counts[warningType];

  if (!warningCounts) return 0;

  return Object.values(warningCounts).reduce((prev, count) => {
    return prev + count;
  }, 0);
};

export const getCrewShiftsWarnings = (shifts: CrewMemberWithShifts[]) => {
  const warnings = shifts.reduce<ShiftsCrewWarningsType>((acc, crew) => {
    if (!acc[crew.id] as any) acc[crew.id] = [];

    if (Array.isArray(crew.dates) && crew.dates.length) {
      crew.dates
        .filter(d => d.warnings?.length)
        .forEach(d => {
          acc[crew.id] = [
            ...acc[crew.id],
            ...(d.warnings || []).map(w => ({
              ...w,
              date: d.date,
              sort_index: d.sort_index
            }))
          ];
        });
    }

    return acc;
  }, {});

  return warnings;
};

export const isDateToday = (date: string): boolean => {
  const parsed = parseISO(date.slice(0, 10));

  if (!isValid(parsed)) return false;

  return isSameDay(parsed, new Date());
};

export const getCrewContractDateRange = (
  signOnDate: string | undefined,
  signOffDate: string | undefined
): Date[] | undefined => {
  if (!signOnDate) return undefined;

  const startDate = new Date(signOnDate);
  const endDate = signOffDate ? new Date(signOffDate) : new Date();

  const dates: Date[] = [];
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

export const getCrewMatrixRuleInfo = (label: string) => {
  switch (label) {
    case 'company_service':
      return 'The sum of the duration of all seaman contracts with the Company.';
    case 'rank_service':
      return 'The sum of the duration of all seaman contracts in the specific rank.';
    case 'tanker_type_service':
      return 'The sum of the duration of all seaman contracts in the specific Type of Tanker.';
    case 'tanker_service':
      return 'The sum of the duration of all seaman contracts in Tankers.';
    case 'bulk_carrier_type_service':
      return 'The sum of the duration of all seaman contracts in Bulk Carriers.';
    case 'total_sea_service':
      return 'The sum of the duration of all seaman contracts.';
    case 'date_joined':
      return 'The difference of the sign on dates between the specified ranks.';
    case 'years_operator':
      return 'The duration since the first sign on date of the seaman with a vessel of the company.';
    default:
      return '';
  }
};

export const statusesInfo = {
  onboard: {
    className: 'bg-yellow text-primary',
    hasVessel: true
  },
  rejected: {
    className: 'bg-light-coral text-coral'
  },
  in_rotation: {
    hasVessel: true
  }
};

export const hiddenStatuses = [
  { label: 'in_rotation', name: 'IN ROTATION' },
  { label: 'onboard', name: 'ON BOARD' }
];

export const statusesClasses = [
  { label: 'onboard', className: 'bg-yellow text-primary' },
  { label: 'rejected', className: 'bg-light-coral text-coral' }
];

export const shouldShowCrewStatusTerminationReason = (action: CrewStatusAction) =>
  action === 'terminated';

export const getCrewRotationStateInfo = (crewRotation: CrewRotation) => {
  return {
    off_signer_will_be_replaced:
      crewRotation.intend_to_replace_off_signer &&
      !crewRotation.is_off_signer_replacement_via_promotion,
    off_signer_will_be_internally_replaced_via_promotion:
      !crewRotation.intend_to_replace_off_signer &&
      crewRotation.is_off_signer_replacement_via_promotion,
    off_signer_was_internally_promoted: !!(
      crewRotation.promotion_crew_rotation && crewRotation.promotion_crew_rotation.finalized_at
    ),
    off_signer_will_be_internally_promoted: !!(
      crewRotation.promotion_crew_rotation &&
      crewRotation.promotion_crew_rotation.finalized_at === null
    ),
    off_signer_wont_be_replaced:
      crewRotation.wont_be_replaced === true ||
      (!crewRotation.intend_to_replace_off_signer &&
        !crewRotation.is_off_signer_replacement_via_promotion)
  };
};

export const getOffSignerActiveContract = (
  contracts: CrewContract[] | null | undefined
): CrewContract | null => {
  if (!contracts || contracts.length === 0) return null;

  const activeContractIsActive = contracts.find(c => c.is_active);
  if (activeContractIsActive) return activeContractIsActive;

  const now = new Date();

  const activeContract = contracts.find(c => {
    const from = c.contract_from_date ? startOfDay(parseISO(c.contract_from_date)) : null;
    const to = c.contract_to_date ? endOfDay(parseISO(c.contract_to_date)) : null;
    if (!from || !to) return false;
    return (
      (isAfter(now, from) || isSameDay(now, from)) && (isBefore(now, to) || isSameDay(now, to))
    );
  });

  if (activeContract) return activeContract;

  return contracts[0];
};

export const selectContractDate = (contract: CrewContract, isSignOn = true): string | undefined => {
  const key = isSignOn ? 'sign_on' : 'sign_off';

  const dateAsPerCaptain = _get(contract, `${key}_date_as_per_captain`);
  const signDate = _get(contract, `${key}_date`);

  // If we have both then always show sign office date
  if (dateAsPerCaptain && signDate) {
    return signDate;
  }

  return dateAsPerCaptain || signDate;
};

export const selectContractPort = (contract: CrewContract, isSignOn = true): Port | undefined => {
  const key = isSignOn ? 'sign_on' : 'sign_off';

  const portAsPerCaptain = _get(contract, `${key}_port`);
  const signPort = _get(contract, `${key}_port_as_per_office`);

  // If we have both then always show sign office port
  if (portAsPerCaptain && signPort) {
    return signPort;
  }

  return portAsPerCaptain || signPort;
};
